import { Injectable, inject, signal } from '@angular/core';
import { Auth, authState } from '../core/firebase/auth';
import {
  Observable,
  catchError,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { UserApiService } from './user-api.service';
import { AdminApiService } from './admin-api.service';
import { markInternalDevice } from '../core/internal-traffic';
import { StaffAccess, StaffPermission } from '../types/rest-api';

@Injectable({ providedIn: 'root' })
export class StaffService {
  private readonly userApi = inject(UserApiService);
  private readonly adminApi = inject(AdminApiService);
  private readonly user$ = authState(inject(Auth));
  private readonly state = signal<boolean>(false);
  private cached: { uid: string; request$: Observable<boolean> } | null = null;
  private readonly accessState = signal<StaffAccess | null>(null);
  private cachedAccess: {
    uid: string;
    request$: Observable<StaffAccess | null>;
  } | null = null;

  readonly isStaff = this.state.asReadonly();
  /** Role and permissions from GET /v1/admin/me; null until loaded or for non-staff. */
  readonly access = this.accessState.asReadonly();

  can(permission: StaffPermission): boolean {
    return this.accessState()?.permissions.includes(permission) ?? false;
  }

  /** Keeps the staff role per signed-in user; a null answer is not kept, so a failed account load is retried. Non-staff never call /admin/me. */
  ensureAccess(): Observable<StaffAccess | null> {
    return this.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          this.cachedAccess = null;
          this.accessState.set(null);
          return of(null);
        }
        if (this.cachedAccess?.uid !== user.uid) {
          this.cachedAccess = {
            uid: user.uid,
            request$: this.loadAccess(user.uid),
          };
        }
        return this.cachedAccess.request$;
      })
    );
  }

  /** Caches the answer per signed-in user; a logged-out check or a failed call is not kept. */
  ensureLoaded(): Observable<boolean> {
    return this.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user) {
          this.cached = null;
          this.state.set(false);
          return of(false);
        }
        if (this.cached?.uid !== user.uid) {
          this.cached = { uid: user.uid, request$: this.load(user.uid) };
        }
        return this.cached.request$;
      })
    );
  }

  private load(uid: string): Observable<boolean> {
    return this.userApi.getAccount().pipe(
      map(account => account.is_staff === true),
      tap(value => {
        if (this.cached?.uid !== uid) return;
        this.state.set(value);
        if (value) markInternalDevice();
      }),
      catchError(() => {
        if (this.cached?.uid === uid) {
          this.cached = null;
          this.state.set(false);
        }
        return of(false);
      }),
      shareReplay(1)
    );
  }

  private loadAccess(uid: string): Observable<StaffAccess | null> {
    return this.ensureLoaded().pipe(
      switchMap(isStaff => (isStaff ? this.adminApi.getMe() : of(null))),
      tap(access => {
        if (this.cachedAccess?.uid !== uid) return;
        this.accessState.set(access);
        if (access === null) this.cachedAccess = null;
      }),
      catchError(() => {
        if (this.cachedAccess?.uid === uid) {
          this.cachedAccess = null;
          this.accessState.set(null);
        }
        return of(null);
      }),
      shareReplay(1)
    );
  }
}
